import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const badgeVariants = cva(
    "inline-flex items-center rounded-md border px-2.5 py-0.5 text-sm transition-colors focus:outline-hidden focus:ring-2 focus:ring-ring focus:ring-offset-2",
    {
        variants: {
            variant: {
                default:
                    "border-transparent bg-slate-100 dark:bg-white/10 text-black dark:text-white hover:bg-slate-100 dark:hover:bg-white/15 font-semibold",
                secondary:
                    "border-transparent bg-transparent text-black/50 dark:text-white/50 hover:bg-slate-50 dark:hover:bg-white/10",
                destructive:
                    "border-transparent bg-destructive text-destructive-foreground shadow hover:bg-destructive/80",
                outline: "text-foreground",
                confidence: "border-transparent bg-green-500/15 text-green-500",
                highlight:
                    "border-transparent bg-orange-500/15 text-orange-600 dark:text-orange-400 font-semibold",
                ultra: "relative overflow-hidden border-transparent bg-gradient-to-br from-slate-900 via-black to-slate-800 text-transparent bg-clip-text bg-gradient-to-r from-amber-400 via-yellow-500 to-amber-600 font-extrabold tracking-wide shadow-2xl hover:shadow-amber-500/30 hover:shadow-3xl hover:scale-110 transform transition-all duration-700 ease-out backdrop-blur-sm before:absolute before:inset-0 before:bg-gradient-to-r before:from-transparent before:via-amber-400/20 before:to-transparent before:translate-x-[-200%] hover:before:translate-x-[200%] before:transition-transform before:duration-1000 after:absolute after:inset-[2px] after:bg-gradient-to-br after:from-black/95 after:via-slate-900/95 after:to-black/95 after:rounded-[inherit] after:-z-10 hover:after:from-black/90 hover:after:via-slate-800/90 hover:after:to-black/90 text-white",
                premium:
                    "relative overflow-hidden border-transparent bg-gradient-to-br from-slate-900 via-black to-slate-800 text-transparent bg-clip-text bg-gradient-to-r from-amber-400 via-yellow-500 to-amber-600 font-extrabold tracking-wide shadow-2xl hover:shadow-amber-500/30 hover:shadow-3xl hover:scale-110 transform transition-all duration-700 ease-out backdrop-blur-sm before:absolute before:inset-0 before:bg-gradient-to-r before:from-transparent before:via-amber-400/20 before:to-transparent before:translate-x-[-200%] hover:before:translate-x-[200%] before:transition-transform before:duration-1000 after:absolute after:inset-[2px] after:bg-gradient-to-br after:from-black/95 after:via-slate-900/95 after:to-black/95 after:rounded-[inherit] after:-z-10 hover:after:from-black/90 hover:after:via-slate-800/90 hover:after:to-black/90 text-white",
            },
            size: {
                sm: "text-xs px-2 py-1",
                default: "text-sm px-2.5 py-1.5",
                xl: "text-base px-4 py-1.5",
            },
        },
        defaultVariants: {
            variant: "default",
            size: "default",
        },
    },
);

export interface BadgeProps
    extends React.HTMLAttributes<HTMLDivElement>,
        VariantProps<typeof badgeVariants> {}

function Badge({ className, variant, size, ...props }: BadgeProps) {
    return (
        <div
            className={cn(badgeVariants({ variant, size }), className)}
            {...props}
        />
    );
}

export { Badge, badgeVariants };
